// Pencil Tool — hard-edge variant of Brush (no anti-aliasing, 1px minimum).
// Shares the same shortcut (B) and cycles with Brush via Shift+B.

import { PaintStrokeCommand, snapshotRegion } from "../commands/PaintStrokeCommand";
import { SetSelectionCommand } from "../commands/SetSelectionCommand";
import type { RasterLayer } from "../document/Layer";
import { findRaster } from "../document/LayerUtils";
import { Selection } from "../selection/Selection";
import { mergeBBox, stamp, stampLine, type BBox, type BrushMode } from "../pixel/brush";
import { screenToDoc } from "../render/Viewport";
import type { PointerSample, Tool, ToolContext } from "./Tool";

export class PencilTool implements Tool {
  readonly id = "pencil";
  readonly shortcut = "B";
  private active: {
    layer: RasterLayer;
    layerSnapshot: Uint8ClampedArray;
    lastX: number;
    lastY: number;
    bbox: BBox | null;
    /**
     * Layer-local doc coords where the current drag began (set once in
     * onPointerDown). Used as the anchor for Shift+drag 45°-axis constraint
     * mid-stroke (audit finding 167) — mirrors BrushTool's identical logic
     * (BrushTool.ts onPointerMove).
     */
    strokeStartX: number;
    strokeStartY: number;
  } | null = null;

  private prevStrokeX: number | null = null;
  private prevStrokeY: number | null = null;

  /** Pencil always uses size 1 (or the configured size with no anti-aliasing). */
  size = 1;
  /** Options-bar Opacity (0..1) — like Brush minus Flow/Airbrush (issue #70). */
  opacity = 1;
  /**
   * Options-bar "Mode" blend mode (issue #70), mirrors Brush's Mode dropdown.
   * Not yet consumed by the pixel compositor (same known gap as Brush's own
   * blendMode — see PAINTING_BLEND_MODES doc comment in ToolUX.ts).
   */
  blendMode = "normal";

  private getSelMask(ctx: ToolContext): { mask: Uint8Array | undefined; w: number | undefined } {
    const sel = (ctx.doc as any)?.selection as { mask?: Uint8Array } | null;
    if (!sel || !sel.mask) return { mask: undefined, w: undefined };
    return { mask: sel.mask, w: ctx.doc!.width };
  }

  onPointerDown(ctx: ToolContext, s: PointerSample): void {
    if (!ctx.doc || !ctx.doc.activeLayerId) return;
    const layer = findRaster(ctx.doc.layers, ctx.doc.activeLayerId);
    if (!layer || !layer.visible) return;
    if (layer.lockAll || layer.lockImage) {
      ctx.showTip("Layer is locked.");
      return;
    }

    const p = screenToDoc(ctx.viewport, s.x, s.y);

    if (s.shiftKey && this.prevStrokeX !== null && this.prevStrokeY !== null) {
      this.strokeLine(ctx, layer, this.prevStrokeX, this.prevStrokeY, p.x, p.y, s);
      this.prevStrokeX = p.x;
      this.prevStrokeY = p.y;
      layer.pixels.markDirty();
      ctx.requestRender();
      return;
    }

    const docX = p.x - layer.x;
    const docY = p.y - layer.y;
    const snapshot = new Uint8ClampedArray(layer.pixels.data);
    this.active = {
      layer, layerSnapshot: snapshot, lastX: docX, lastY: docY, bbox: null,
      strokeStartX: docX, strokeStartY: docY,
    };

    const { mask: selMask, w: selMaskW } = this.getSelMask(ctx);
    const lockAlpha = layer.lockTransparency ? extractAlpha(layer.pixels) : undefined;
    const lockAlphaW = lockAlpha ? layer.pixels.width : undefined;

    const bb = stamp(layer.pixels, {
      cx: docX, cy: docY,
      radius: this.size / 2,
      hardness: 1, // fully hard — no anti-aliasing
      opacity: this.opacity,
      r: ctx.fgColor.r, g: ctx.fgColor.g, b: ctx.fgColor.b,
      mode: "paint" as BrushMode,
      selMask, selMaskW, lockAlpha, lockAlphaW,
    });
    if (bb) this.active.bbox = bb;
    layer.pixels.markDirty();
    ctx.requestRender();
  }

  onPointerMove(ctx: ToolContext, s: PointerSample): void {
    if (!this.active) return;
    const p = screenToDoc(ctx.viewport, s.x, s.y);
    let docX = p.x - this.active.layer.x;
    let docY = p.y - this.active.layer.y;

    // Shift+drag mid-stroke: constrain the current pointer position to the
    // nearest 45° increment measured from the stroke's start point, so the
    // whole drag paints a straight horizontal/vertical/diagonal line — same
    // angle-snap math as BrushTool (audit finding 167).
    if (s.shiftKey) {
      const anchorX = this.active.strokeStartX;
      const anchorY = this.active.strokeStartY;
      const dx = docX - anchorX;
      const dy = docY - anchorY;
      const angle = Math.atan2(dy, dx);
      const snapped = Math.round(angle / (Math.PI / 4)) * (Math.PI / 4);
      const dist = Math.hypot(dx, dy);
      docX = anchorX + Math.cos(snapped) * dist;
      docY = anchorY + Math.sin(snapped) * dist;
    }

    const { mask: selMask, w: selMaskW } = this.getSelMask(ctx);
    const lockAlpha = this.active.layer.lockTransparency ? extractAlpha(this.active.layer.pixels) : undefined;
    const lockAlphaW = lockAlpha ? this.active.layer.pixels.width : undefined;

    const bb = stampLine(
      this.active.layer.pixels,
      {
        radius: this.size / 2,
        hardness: 1, // fully hard
        opacity: this.opacity,
        r: ctx.fgColor.r, g: ctx.fgColor.g, b: ctx.fgColor.b,
        mode: "paint" as BrushMode,
        selMask, selMaskW, lockAlpha, lockAlphaW,
      },
      this.active.lastX, this.active.lastY, docX, docY,
      Math.max(1, this.size),
    );

    if (bb) this.active.bbox = this.active.bbox ? mergeBBox(this.active.bbox, bb) : bb;
    this.active.lastX = docX;
    this.active.lastY = docY;
    this.active.layer.pixels.markDirty();
    ctx.requestRender();
  }

  onPointerUp(ctx: ToolContext): void {
    if (!this.active) return;
    if (this.active.layer) {
      this.prevStrokeX = this.active.lastX + this.active.layer.x;
      this.prevStrokeY = this.active.lastY + this.active.layer.y;
    }
    if (!this.active.bbox) { this.active = null; return; }
    const { layer, layerSnapshot, bbox } = this.active;
    const after = snapshotRegion(layer, bbox);
    const before = extractRegion(layerSnapshot, layer.pixels.width, bbox);
    this.active = null;
    ctx.emit(new PaintStrokeCommand(layer.id, bbox, before, after));
  }

  private strokeLine(
    ctx: ToolContext, layer: RasterLayer,
    fromX: number, fromY: number, toX: number, toY: number, s: PointerSample,
  ): void {
    const docFromX = fromX - layer.x, docFromY = fromY - layer.y;
    const docToX = toX - layer.x, docToY = toY - layer.y;
    const snapshot = new Uint8ClampedArray(layer.pixels.data);
    const { mask: selMask, w: selMaskW } = this.getSelMask(ctx);
    const lockAlpha = layer.lockTransparency ? extractAlpha(layer.pixels) : undefined;
    const lockAlphaW = lockAlpha ? layer.pixels.width : undefined;

    const bb = stampLine(
      layer.pixels,
      {
        radius: this.size / 2, hardness: 1, opacity: this.opacity,
        r: ctx.fgColor.r, g: ctx.fgColor.g, b: ctx.fgColor.b,
        mode: "paint" as BrushMode, selMask, selMaskW, lockAlpha, lockAlphaW,
      },
      docFromX, docFromY, docToX, docToY, Math.max(1, this.size),
    );

    if (bb) {
      const after = snapshotRegion(layer, bb);
      const before = extractRegion(snapshot, layer.pixels.width, bb);
      ctx.emit(new PaintStrokeCommand(layer.id, bb, before, after));
    }
  }
}

function extractRegion(full: Uint8ClampedArray, fullWidth: number, bbox: BBox): Uint8ClampedArray {
  const out = new Uint8ClampedArray(bbox.w * bbox.h * 4);
  for (let row = 0; row < bbox.h; row++) {
    const srcOff = ((bbox.y + row) * fullWidth + bbox.x) * 4;
    const dstOff = row * bbox.w * 4;
    out.set(full.subarray(srcOff, srcOff + bbox.w * 4), dstOff);
  }
  return out;
}

function extractAlpha(buf: { data: Uint8ClampedArray; width: number }): Uint8Array {
  const len = buf.data.length;
  const out = new Uint8Array(len / 4);
  for (let i = 3, j = 0; i < len; i += 4, j++) out[j] = buf.data[i]!;
  return out;
}
